import { useState } from 'react';
import { Check, Copy, Download, Info, TriangleAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { LOW_CONFIDENCE_THRESHOLD, LOW_RESOLUTION_NOTICE, getLanguageOption } from '../../lib/ocr/constants';

const downloadText = (filename, text) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const baseName = (name) => name.replace(/\.[^.]+$/, '') || 'extracted-text';

const CopyButton = ({ text, testId, label = 'Copy text' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      data-testid={testId}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch (e) {
          const area = document.createElement('textarea');
          area.value = text;
          document.body.appendChild(area);
          area.select();
          document.execCommand('copy');
          area.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
      size="sm"
      type="button"
      variant="outline"
    >
      {copied ? (
        <Check aria-hidden="true" className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
      ) : (
        <Copy aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
      )}
      {copied ? 'Copied' : label}
    </Button>
  );
};

const ResultCard = ({ item, onTextChange }) => {
  const result = item.result;
  const text = item.editedText ?? result.text;
  const confidence = result.averageConfidence;
  const lowLines = (result.lines || []).filter(
    (l) => typeof l.confidence === 'number' && l.confidence < LOW_CONFIDENCE_THRESHOLD,
  );

  return (
    <article className="surface-card p-4 sm:p-5" data-testid={`result-card-${item.id}`}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-bold text-slate-900" title={item.name}>
            {item.name}
          </h3>
          <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            <span>{result.mode === 'poster' ? 'Poster mode' : 'Document mode'}</span>
            {result.durationMs ? <span>{(result.durationMs / 1000).toFixed(1)}s</span> : null}
            {result.pdfPages ? <span>{result.pdfPages.length} pages</span> : null}
            {result.tables?.length ? <span>{result.tables.length} table(s)</span> : null}
            {result.invertedForDarkBackground ? <span>dark background corrected</span> : null}
          </p>
        </div>
        {typeof confidence === 'number' ? (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              confidence >= LOW_CONFIDENCE_THRESHOLD
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-800'
            }`}
            data-testid={`result-confidence-badge-${item.id}`}
            title="Engine confidence. This is an estimate, not a guarantee of accuracy."
          >
            Confidence ≈ {confidence.toFixed(0)}%
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
            Confidence not reported
          </span>
        )}
      </header>

      <dl
        className="mt-3 grid gap-x-4 gap-y-2 rounded-xl bg-[#f6f7fd] px-3 py-2.5 text-xs sm:grid-cols-2"
        data-testid={`result-metadata-${item.id}`}
      >
        <div className="flex gap-1.5">
          <dt className="shrink-0 font-semibold text-slate-700">Engine used</dt>
          <dd className="truncate text-slate-600" data-testid={`meta-engine-${item.id}`} title={result.engineLabel}>
            {result.engineLabel}
          </dd>
        </div>
        <div className="flex gap-1.5">
          <dt className="shrink-0 font-semibold text-slate-700">Language model</dt>
          <dd className="truncate text-slate-600" data-testid={`meta-language-${item.id}`}>
            {(result.languageConfig || []).join(' + ')} · {getLanguageOption(result.languageId).shortLabel}
          </dd>
        </div>
        <div className="flex gap-1.5">
          <dt className="shrink-0 font-semibold text-slate-700">Preprocessing</dt>
          <dd className="truncate text-slate-600" data-testid={`meta-variant-${item.id}`}>
            {result.preprocessVariant}
          </dd>
        </div>
        <div className="flex gap-1.5">
          <dt className="shrink-0 font-semibold text-slate-700">Segmentation</dt>
          <dd className="truncate text-slate-600" data-testid={`meta-segmentation-${item.id}`}>
            {result.segmentationMode}
          </dd>
        </div>
      </dl>

      {result.lowResolution ? (
        <p
          className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900"
          data-testid={`low-resolution-notice-${item.id}`}
        >
          <TriangleAlert aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {LOW_RESOLUTION_NOTICE}
        </p>
      ) : null}

      <label className="sr-only" htmlFor={`text-${item.id}`}>
        Extracted text for {item.name}
      </label>
      <Textarea
        className="ocr-output mt-4 min-h-[190px] resize-y text-sm leading-relaxed"
        data-testid={`result-editable-textarea-${item.id}`}
        id={`text-${item.id}`}
        onChange={(e) => onTextChange(item.id, e.target.value)}
        spellCheck={false}
        value={text}
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <CopyButton testId={`result-copy-button-${item.id}`} text={text} />
        <Button
          data-testid={`result-download-button-${item.id}`}
          onClick={() => downloadText(`${baseName(item.name)}.txt`, text)}
          size="sm"
          type="button"
          variant="outline"
        >
          <Download aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
          Download .txt
        </Button>
        <span className="text-xs text-slate-500">
          {text.split('\n').filter(Boolean).length} lines · {text.length} characters
        </span>
      </div>

      {result.warnings?.length ? (
        <ul className="mt-3 space-y-1 rounded-xl bg-[#f5f6fd] px-3 py-2 text-xs text-slate-600">
          {result.warnings.map((w) => (
            <li className="flex items-start gap-2" key={w}>
              <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-600" />
              {w}
            </li>
          ))}
        </ul>
      ) : null}

      {lowLines.length ? (
        <details className="mt-3 rounded-xl bg-amber-50 px-3 py-2" data-testid={`review-lines-${item.id}`}>
          <summary className="cursor-pointer text-xs font-semibold text-amber-900">
            Review recommended · {lowLines.length} line{lowLines.length > 1 ? 's' : ''} below{' '}
            {LOW_CONFIDENCE_THRESHOLD}% confidence
          </summary>
          <ul className="mt-2 space-y-1">
            {lowLines.slice(0, 25).map((line, idx) => (
              <li className="ocr-output text-xs text-amber-900" key={`${idx}-${line.text}`}>
                {line.text}
                <span className="ml-2 font-sans text-[11px] font-semibold text-amber-700">
                  {line.confidence.toFixed(0)}%
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-amber-800">
            Confidence is an engine estimate. Low confidence does not always mean the line is wrong, and high
            confidence is not a guarantee.
          </p>
        </details>
      ) : null}
    </article>
  );
};

export const ResultsPanel = ({ items, copyAllText, onTextChange }) => {
  if (!items.length) return null;
  return (
    <section aria-labelledby="results-heading" className="scroll-mt-24" data-testid="result-card-container" id="results">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" id="results-heading">
            Extracted text
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Every result is editable. Nothing here is translated, summarised or spell-corrected.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CopyButton label="Copy all results" testId="copy-all-button" text={copyAllText} />
          <Button
            data-testid="download-all-button"
            onClick={() => downloadText('text-extractor-results.txt', copyAllText)}
            size="sm"
            type="button"
            variant="outline"
          >
            <Download aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
            Download all
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-4">
        {items.map((item) => (
          <ResultCard item={item} key={item.id} onTextChange={onTextChange} />
        ))}
      </div>
    </section>
  );
};

export default ResultsPanel;
